import { Map } from "immutable";
import { cloneDeep, find, isNil } from "lodash";

import compactDisplays from "../../constants/compactDisplay";
import { DateOrderIdentifiers } from "../../constants/dateOrders";
import { TIMELINE_VIEW } from "../../constants/identifiers";
import itemTypeCategories from "../../constants/itemTypeCategories";
import lineStyles from "../../constants/lineStyles";
import { PropertyFormatIdentifier } from "../../constants/propertyFormats";
import { TimelineZoomLimits } from "../../constants/zoomRange";
import loadData from "../../loadData";
import { definitionSelectors } from "../../modules/definition";
import { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import dateByAddingSpan from "../../modules/definition/calendar/dateFunctions/dateByAddingSpan";
import { padWithZeros } from "../../modules/definition/calendar/dateFunctions/dateConversion";
import dateSubtractionFunctions from "../../modules/definition/calendar/dateFunctions/dateSubtractionFunctions";
import { dateWithString } from "../../modules/definition/calendar/dateFunctions/dateWithString";
import dateWithTimestamp from "../../modules/definition/calendar/dateFunctions/dateWithTimestamp";
import { timespanForString } from "../../modules/definition/calendar/dateFunctions/timespanFunctions";
import {
  getZoomFromUnitScales,
  getZoomToUnitScales,
  unitScaleForZoomLimit
} from "../../modules/definition/calendar/dateFunctions/unitScaleFunctions";
import { createGregorianCalendar } from "../../modules/definition/calendar/gregorianCalendar";
import { getZeroDateTimestampFromCalendar } from "../../modules/definition/calendar/selectors";
import { sequenceIndexesAfterDateChange } from "../../modules/sequence/utils";
import { appState } from "../../store/appState";
import { Precision } from "../../types";
import calendarTypes from "../../types/calendarTypes";
import { PointRecord } from "../../types/immutableRecords/helper/PointRecord";
import { TimelineViewOptionsRecord } from "../../types/immutableRecords/model/appState/TimelineViewOptionsRecord";
import { TimespanRecord } from "../../types/immutableRecords/model/data/TimespanRecord";
import { PreferencesRecord } from "../../types/immutableRecords/model/preferences/PreferencesRecord";
import arrayToListObjectUniqueId from "../../utils/arrayToListObjectUniqueId";
import { idByTypeReferenceGenerator, idReferenceGenerator } from "../../utils/idByReferenceGenerator";
import uniqueId from "../../utils/uniqueId";
import standardValidator from "./standardValidator";

const TIMELINE_SCROLL_START_OFFSET = 100;
const TIMELINE_TOTAL_SPAN_SCALE = 1000;

const defaultColors = {
  byId: {
    "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC": {
      id: "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC",
      label: "Light Purple",
      swatchColor: "lightPurple"
    },
    "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5": {
      id: "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5",
      label: "Light Teal",
      swatchColor: "lightTeal"
    },
    "88F01362-D7BA-4AD9-8C93-E0EC45A604B4": {
      id: "88F01362-D7BA-4AD9-8C93-E0EC45A604B4",
      label: "Denim",
      swatchColor: "denim"
    },
    "D6DB1C49-D913-4D7C-884B-1F7AF1F53187": {
      id: "D6DB1C49-D913-4D7C-884B-1F7AF1F53187",
      label: "Yellow",
      swatchColor: "yellow"
    },
    "199ABF77-578A-4EA8-8C2D-8EA080610A78": {
      id: "199ABF77-578A-4EA8-8C2D-8EA080610A78",
      label: "Dark Purple",
      swatchColor: "darkPurple"
    },
    "D9DAECFF-F3CE-4399-9690-5E045E8BFA49": {
      id: "D9DAECFF-F3CE-4399-9690-5E045E8BFA49",
      label: "Light Blue",
      swatchColor: "lightBlue"
    },
    "0CAFEDAF-A92E-4012-9E43-438B826F5FC7": {
      id: "0CAFEDAF-A92E-4012-9E43-438B826F5FC7",
      label: "Apple",
      swatchColor: "apple"
    },
    "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7": {
      id: "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7",
      label: "Red",
      swatchColor: "red"
    },
    "7F4245A2-AF3B-4633-BEBA-68494486C574": {
      id: "7F4245A2-AF3B-4633-BEBA-68494486C574",
      label: "Peach",
      swatchColor: "peach"
    }
  },
  allIds: [
    "0CAFEDAF-A92E-4012-9E43-438B826F5FC7",
    "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC",
    "199ABF77-578A-4EA8-8C2D-8EA080610A78",
    "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5",
    "7F4245A2-AF3B-4633-BEBA-68494486C574",
    "88F01362-D7BA-4AD9-8C93-E0EC45A604B4",
    "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7",
    "D6DB1C49-D913-4D7C-884B-1F7AF1F53187",
    "D9DAECFF-F3CE-4399-9690-5E045E8BFA49"
  ]
};

const setIdReferences = obj => {
  return idReferenceGenerator(obj).toJS();
};

const setIdTypeReferences = obj => {
  return idByTypeReferenceGenerator(obj).toJS();
};

// App gives JS error if allIdsForType doesn't contain every single type
const fillEmptyItemTypes = (items, types) => {
  types.map(type => {
    items["allIdsForType"][type.id] =
      typeof items["allIdsForType"][type.id] === "undefined" ? [] : items["allIdsForType"][type.id];
  });
  return items;
};

const findTimestampRangeOfItems = (calendar, items) => {
  let earliestTs = null;
  let latestTs = null;

  items.forEach(i => {
    if (isDateValid(i.startDate)) {
      const startTs = i.startDate.timestamp;
      earliestTs = earliestTs ? Math.min(earliestTs, startTs) : startTs;
      if (i.duration) {
        const endTs = dateByAddingSpan(calendar, i.startDate, TimespanRecord(i.duration)).timestamp;
        latestTs = latestTs ? Math.max(latestTs, endTs) : endTs;
      }
    }
    return earliestTs;
  });
  return { earliestTs, latestTs };
};

const getZoomRange = (calendar, definitions) => {
  const zoomFrom =
    definitions.timelineViewSettings && !isNil(definitions.timelineViewSettings.zoomFrom)
      ? definitions.timelineViewSettings.zoomFrom
      : TimelineZoomLimits.Decade;
  const maxUnitScale = unitScaleForZoomLimit(calendar, zoomFrom, true);
  const zoomTo =
    definitions.timelineViewSettings && !isNil(definitions.timelineViewSettings.zoomTo)
      ? definitions.timelineViewSettings.zoomTo
      : TimelineZoomLimits.Day;
  const minUnitScale = unitScaleForZoomLimit(calendar, zoomTo, false);
  return { zoomFrom, zoomTo, maxUnitScale, minUnitScale };
};

/**
 * Check if any timeline view has no timeline options,
 * update that by the according to the items' date ranges.
 * Also update the zoom range accordingly.
 * Note:
 *   May change the zoom range in definitions.
 *   May change view's timeline options.
 */
const updateTimelineOptions = (appState, calendar, definitions, items) => {
  if (
    Object.values(appState.desktopOpenViews.byId).find(
      v =>
        v.viewType === TIMELINE_VIEW &&
        (!v.timelineOptions || !v.timelineOptions.unitScale || !v.timelineOptions.scrollPosition)
    )
  ) {
    // Find the timestamp range of all items
    const { earliestTs, latestTs } = findTimestampRangeOfItems(calendar, items);

    const { zoomFrom, zoomTo, maxUnitScale, minUnitScale } = getZoomRange(calendar, definitions);

    const calcUnitScale = latestTs
      ? (latestTs - earliestTs) / TIMELINE_TOTAL_SPAN_SCALE
      : definitionSelectors.getDefaultUnitScaleWithParams(calendar, zoomFrom, zoomTo);

    // May need to adjust the zoom range
    if (latestTs) {
      if (calcUnitScale > maxUnitScale) {
        // Find the bigger zoom range
        // update zoomFrom
        const newZoomFrom = getZoomFromUnitScales(calendar)
          .sort((a, b) => a.value - b.value)
          .find(u => u.value > calcUnitScale).key;
        definitions.timelineViewSettings.zoomFrom = newZoomFrom;
      } else if (calcUnitScale < minUnitScale) {
        // Find the smaller zoom range
        // update zoomTo
        const newZoomTo = getZoomToUnitScales(calendar)
          .sort((a, b) => b.value - a.value)
          .find(u => u.value < calcUnitScale).key;
        definitions.timelineViewSettings.zoomTo = newZoomTo;
      }
    }

    // Update the timeline options
    if (!isNil(earliestTs)) {
      Object.values(appState.desktopOpenViews.byId).forEach(v => {
        if (
          v.viewType === TIMELINE_VIEW &&
          (!v.timelineOptions || !v.timelineOptions.unitScale || v.timelineOptions.scrollPosition)
        ) {
          v.timelineOptions = TimelineViewOptionsRecord({
            unitScale: calcUnitScale,
            scrollPosition: PointRecord({ x: earliestTs / calcUnitScale - TIMELINE_SCROLL_START_OFFSET, y: 0 })
          });
        }
      });
    }
  }
};

const ID_DEFAULT_PREFIX = "default";
const defaultLength = ID_DEFAULT_PREFIX.length;

const getIdPrefix = id => {
  let prefix = id.toLowerCase();
  prefix =
    prefix.startsWith(ID_DEFAULT_PREFIX) && prefix.length > defaultLength
      ? prefix.replace(ID_DEFAULT_PREFIX, "")
      : prefix;

  prefix = prefix.substring(0, 2).toUpperCase();
  return prefix;
};

const standardToV3Converter = jsonData => {
  let calendar = createGregorianCalendar();
  const obj = typeof jsonData === "string" ? JSON.parse(jsonData) : jsonData;
  const typeCounts = [];
  const aeonTimelineData = obj.appData ? obj.appData.aeonTimeline : null;

  // Validate
  const valid = standardValidator(jsonData);
  if (!valid) {
    return false;
  }

  // Definition > isRelative and relativeUnits

  const isRelative = obj.definition.isRelative ? obj.definition.isRelative : false; // Bool
  const relativeUnits = obj.definition.relativeUnits; // oneOf

  // Get zeroDate info for relative calendars
  let precision = Precision.Auto;
  switch (relativeUnits) {
    case "weeks":
      precision = Precision.Week;
      break;
    case "days":
      precision = Precision.Day;
      break;
    case "hours":
      precision = Precision.Hour;
      break;
  }

  const zeroTimestamp = getZeroDateTimestampFromCalendar(calendar);
  const zeroDate = dateWithTimestamp(calendar, zeroTimestamp, precision);

  // Set the calendar if relative
  if (isRelative) {
    switch (relativeUnits) {
      case "weeks":
        calendar = calendar.set("calendarType", calendarTypes.CALENDAR_TYPE_WEEKDAY_TIME);
        break;
      case "days":
        calendar = calendar.set("calendarType", calendarTypes.CALENDAR_TYPE_DAY_TIME);
        break;
      case "hours":
        calendar = calendar.set("calendarType", calendarTypes.CALENDAR_TYPE_HOUR_TIME);
        break;
    }
  }

  // Definition > Types

  const types = obj.definition.types.reduce((arr, type) => {
    // For item displayIds
    typeCounts[type.id] = 0;

    let properties = Array.isArray(type.properties) ? type.properties : [];
    const colorIndex = properties.indexOf("color");
    if (colorIndex !== -1) properties.splice(colorIndex, 1);
    const imageIndex = properties.indexOf("image");
    if (imageIndex !== -1) properties.splice(imageIndex, 1);
    const linksIndex = properties.indexOf("links");
    if (linksIndex !== -1) properties.splice(linksIndex, 1);
    const attachmentsIndex = properties.indexOf("attachments");
    if (attachmentsIndex !== -1) properties.splice(attachmentsIndex, 1);
    const summaryIndex = properties.indexOf("summary");
    if (summaryIndex !== -1) properties.splice(summaryIndex, 1);
    const tagsIndex = properties.indexOf("tags");
    if (tagsIndex !== -1) properties.splice(tagsIndex, 1);
    const initialsIndex = properties.indexOf("initials");
    if (initialsIndex !== -1) properties.splice(initialsIndex, 1);

    // Icons and avatars
    const typeData = aeonTimelineData ? aeonTimelineData.types : null;
    const typeObj = typeData ? typeData.find(data => data.id === type.id) : null;

    // If no app type data: If has dates, then showOnSidebar = false, showinSequence = true, showAsColumn = false
    const showOnSidebar = typeObj ? Boolean(typeObj.showOnSidebar) : !type.hasDates;
    const showInSequence = typeObj ? Boolean(typeObj.showInSequence) : type.hasDates;
    const showAsColumn = typeObj ? Boolean(typeObj.showAsColumn) : !type.hasDates;

    arr.push({
      id: type.id, // Required
      label: type.label, // Required
      pluralLabel: type.pluralLabel, // Required
      canHaveDate: Boolean(type.hasDates), // Optional
      superTypes: Array.isArray(type.superTypes) ? type.superTypes : null, // Optional
      properties: properties, // Optional
      references: Array.isArray(type.relationshipTypes) ? type.relationshipTypes : null, // Optional
      isRelative: isRelative,
      relativeUnits: relativeUnits ? relativeUnits : "seconds", // Can't be null or app gives JS error
      canHaveLocationUri: type.isLocation, // Optional
      category: type.isLocation ? itemTypeCategories.LOCATION : itemTypeCategories.OTHER, // Optional

      // Not part of the standard
      enabled: true,
      canDelete: true,
      identifierPrefix: getIdPrefix(type.id), // Can't be null or app gives JS error
      canIncludeInNarrative: false,
      showOnSidebar: showOnSidebar,
      showInSequence: showInSequence,
      showAsColumn: showAsColumn,
      compactDisplay: typeObj && typeObj.compactDisplay ? typeObj.compactDisplay : compactDisplays.IDENTIFIER,
      icon: typeObj && typeObj.icon ? typeObj.icon : "diamond", // Can't be null or app gives JS error
      color: typeObj && typeObj.color ? typeObj.color : "iconMono" // Can't be null or app gives JS error
    });

    return arr;
  }, []);

  // Which types to show on timeline

  const typesWithDates = types
    .filter(type => type.canHaveDate)
    .reduce((arr, type) => {
      arr.push(type.id);
      return arr;
    }, []);

  // Definition > Properties

  const properties = obj.definition.properties.reduce((arr, property) => {
    let format = property.dataFormat;
    switch (format) {
      case "string":
        format = Array.isArray(property.allowedValues) ? "list" : "text";
        break;
      case "number":
        format = "integer";
        break;
    }

    if (!["color", "image", "links", "attachments", "summary", "tags", "initials"].includes(property.id)) {
      arr.push({
        id: property.id, // Required
        label: property.label, // Required
        format: format, // Required, "string", "number", or "boolean"
        allowed: Array.isArray(property.allowedValues) ? arrayToListObjectUniqueId(property.allowedValues) : null, // Optional
        multiple: property.multiple, // Optional, must be false if format = boolean

        // Not part of the standard
        key: property.id,
        enabled: true,
        canDelete: true
      });
    }

    return arr;
  }, []);

  // Definition > References

  const references = obj.definition.relationshipTypes.reduce((arr, relationshipType) => {
    const refData = aeonTimelineData ? aeonTimelineData.references : null;
    const refObj = refData ? refData.find(data => data.id === relationshipType.id) : null;

    arr.push({
      id: relationshipType.id, // Required
      label: relationshipType.label, // Required
      types: Array.isArray(relationshipType.types) ? relationshipType.types : [], // Required
      // multiple: relationshipType.multiple, // Optional, bool

      // Not part of the standard
      key: relationshipType.id,
      enabled: true,
      canDelete: true,
      icon: refObj ? refObj.icon : "circle",
      color: refObj ? refObj.color : "mono",
      lineStyle: refObj ? refObj.lineStyle : lineStyles.SOLID,
      bidirectional: refObj ? refObj.bidirectional : false
    });

    return arr;
  }, []);

  // Definition: Timeline view settings
  let timelineViewSettings = {
    showTypes: typesWithDates
  };
  if (aeonTimelineData && !isNil(aeonTimelineData.timelineViewSettings)) {
    timelineViewSettings = aeonTimelineData.timelineViewSettings;
    timelineViewSettings.showTypes = typesWithDates;
  }

  // Definition: Colors
  let colors = defaultColors;
  if (aeonTimelineData && !isNil(aeonTimelineData.colors)) {
    colors = aeonTimelineData.colors;
  }

  // Definition
  const definitions = {
    types: setIdReferences(types), // Required
    properties: setIdReferences(properties), // Optional
    references: setIdReferences(references), // Optional

    // Not part of the standard
    includeNarrative: false,
    calendar: calendar.toJS(),
    timelineViewSettings: timelineViewSettings,
    colors: colors
  };

  const convertDate = standardDate => {
    let result = undefined;
    if (standardDate) {
      if (!isRelative) {
        let date = standardDate;
        let isBc = false;
        date = date
          .replace("T", " ")
          .replace("Z", " ")
          .replace("+", " ");
        if (date.charAt(0) === "-") {
          date = date.substr(1);
          isBc = true;
        }

        // Convert date to the user's mm/dd dd/mm preference
        const dateParts = date.split(" ");
        if (dateParts.length) {
          const ymdString = dateParts[0];
          const ymdParts = ymdString.split("-");

          let ymdRegex = new RegExp(/(\d{4}-\d{2}-\d{2})/);
          let ymRegex = new RegExp(/(\d{4}-\d{2})/);
          let yRegex = new RegExp(/(\d{4})/);

          let ymdArray = ymdString.match(ymdRegex);
          let ymArray = ymdString.match(ymRegex);
          let yArray = ymdString.match(yRegex);

          // Remove the yyyy-mm-dd string
          dateParts.shift();

          // And replace it with text formatted for dateWithString
          if (parseInt(ymdParts[0]) === 0) {
            // The year 0000 is 1 BC
            isBc = true;
          }
          if (isBc) {
            dateParts.unshift(" BC ");
          }
          if (ymdArray) {
            const yearString = isBc ? padWithZeros(parseInt(ymdParts[0]) + 1, 4) : ymdParts[0];
            const monthString = ymdParts[1];
            const dayString = ymdParts[2];
            if (calendar.dateOrder === DateOrderIdentifiers.DayMonthYear) {
              dateParts.unshift(dayString + "-" + monthString + "-" + yearString);
            } else if (calendar.dateOrder === DateOrderIdentifiers.MonthDayYear) {
              dateParts.unshift(monthString + "-" + dayString + "-" + yearString);
            } else {
              dateParts.unshift(yearString + "-" + monthString + "-" + dayString);
            }
          } else if (ymArray) {
            const yearString = isBc ? padWithZeros(parseInt(ymdParts[0]) + 1, 4) : ymdParts[0];
            const monthString = ymdParts[1];
            if (
              calendar.dateOrder === DateOrderIdentifiers.MonthDayYear ||
              calendar.dateOrder === DateOrderIdentifiers.DayMonthYear
            ) {
              dateParts.unshift(monthString + "-" + yearString);
            } else {
              dateParts.unshift(yearString + "-" + monthString);
            }
          } else if (yArray) {
            const yearString = isBc ? padWithZeros(parseInt(ymdParts[0]) + 1, 4) : ymdParts[0];
            dateParts.unshift(yearString);
          }
        }

        result = dateWithString(calendar, dateParts.join(" "));
      } else {
        const timespan = timespanForString(standardDate + " " + relativeUnits, calendar);
        result = dateByAddingSpan(calendar, zeroDate, timespan);
      }
    }
    return result;
  };

  // Data > Items
  let attachmentData = [];
  let tagData = {};
  let items = obj.data.items.reduce((arr, item) => {
    // Start date
    const start = convertDate(item.start);
    const latestStart = convertDate(item.latestStart);

    // End date
    let duration = undefined;
    let durationToEarliestEnd = null;

    if (start) {
      if (item.end) {
        const end = convertDate(item.end);
        duration = dateSubtractionFunctions.timespanBetweenDates(calendar, start, end);
      }
      if (item.earliestEnd) {
        const earliestEnd = convertDate(item.earliestEnd);
        durationToEarliestEnd = dateSubtractionFunctions.timespanBetweenDates(calendar, start, earliestEnd);
      }
    }

    // Display ID
    const displayId = item.type.substring(0, 2).toUpperCase() + (typeCounts[item.type] + 1);
    typeCounts[item.type]++;

    // Get colors, links, attachments, images and store as properties
    let propertyValues = item.propertyValues;

    const color = Map(colors.byId).find(color => {
      return color.swatchColor === propertyValues["color"];
    });

    let image = null;
    if (propertyValues["image"]) {
      attachmentData.push({
        id: `image-${item.id}`,
        label: `Image`,
        path: propertyValues["image"]
      });
      image = `image-${item.id}`;
    }

    let links = [];
    if (Array.isArray(propertyValues["links"])) {
      for (let i = 0; i < propertyValues["links"].length; i++) {
        attachmentData.push({
          id: `link-${item.id}-${i}`,
          label: null, // `Link ${i + 1}`,
          path: propertyValues["links"][i]
        });
        links.push(`link-${item.id}-${i}`);
      }
    }

    let attachments = [];
    if (Array.isArray(propertyValues["attachments"])) {
      for (let i = 0; i < propertyValues["attachments"].length; i++) {
        attachmentData.push({
          id: `attachment-${item.id}-${i}`,
          label: `Attachment ${i + 1}`,
          path: propertyValues["attachments"][i]
        });
        attachments.push(`attachment-${item.id}-${i}`);
      }
    }

    const summary = propertyValues["summary"];
    const tags = propertyValues["tags"];
    const initials = propertyValues["initials"];

    let tagIds = [];
    if (tags) {
      tags.forEach(tagText => {
        let existingTagId = null;
        for (const key in tagData) {
          if (tagData[key] === tagText) {
            existingTagId = key;
            break;
          }
        }

        if (existingTagId) {
          tagIds.push(existingTagId);
        } else {
          const tagId = uniqueId();
          tagData[tagId] = tagText;
          tagIds.push(tagId);
        }
      });
    }

    delete propertyValues["color"];
    delete propertyValues["image"];
    delete propertyValues["links"];
    delete propertyValues["attachments"];
    delete propertyValues["summary"];
    delete propertyValues["tags"];
    delete propertyValues["initials"];

    // If propertyValue is part of a list, convert it to the correct option ID
    Object.keys(propertyValues).map(function(propId) {
      const prop = definitions.properties.byId[propId];
      const value = propertyValues[propId];
      if (prop.format === "list") {
        const matchingOption = find(prop.allowed, opt => opt.label === value);
        if (matchingOption) {
          propertyValues[propId] = matchingOption.id;
        }
      }
    });

    arr.push({
      id: item.id, // Required
      type: item.type, // Required
      label: item.label, // Required
      preferredStartDate: start ? start.toJS() : undefined, // Optional, number or dateString depending on isRelative
      startDate: start ? start.toJS() : undefined, // Optional, number or dateString depending on isRelative
      latestStartDate: latestStart ? latestStart.toJS() : null,
      preferredDuration: duration ? duration.toJS() : undefined,
      duration: duration ? duration.toJS() : undefined,
      durationToEarliestEnd: durationToEarliestEnd ? durationToEarliestEnd.toJS() : null,
      ongoing: item.ongoing,
      super: item.super, // Optional
      propertyValues: propertyValues, // Optional
      locationUri: item.locationUri, // Optional
      color: color ? color.id : "0CAFEDAF-A92E-4012-9E43-438B826F5FC7",
      image: image ? image : null,
      links: links ? links : [],
      attachments: attachments ? attachments : [],
      summary: summary ? summary : null,
      tags: tagIds,
      initials: initials ? initials : null,

      // Not part of the standard
      displayId: displayId
    });

    return arr;
  }, []);

  let parentIds = [];
  items.forEach(item => {
    if (item.super) {
      parentIds.push(item.super);
    }
  });

  const relationships = obj.data.relationships.reduce((arr, relationship) => {
    // Must fill out item references for relationships to work
    let subject = items.find(item => item.id === relationship.subject);
    if (subject) {
      if (Array.isArray(subject.references)) {
        subject.references.push(relationship.id);
      } else {
        subject.references = [relationship.id];
      }
    }
    //items[items.findIndex(item => item.id === relationship.subject)] = subject;

    arr.push({
      id: relationship.id, // Required
      subject: relationship.subject, // Required
      reference: relationship.relationshipType, // Required
      object: relationship.target // Required
    });

    return arr;
  }, []);

  // Data

  const itemsWithIds = setIdTypeReferences(items);
  let data = {
    items: fillEmptyItemTypes(itemsWithIds, types), // Required
    relationships: setIdReferences(relationships),
    sequence: itemsWithIds.allIds,
    attachments: setIdReferences(attachmentData),
    tags: tagData
  };

  // Order sequence IDs
  const sequencedIndexes = sequenceIndexesAfterDateChange(
    loadData.plainJsToTimelineRecord({
      definitions,
      data,
      appState
    }),
    itemsWithIds.allIds
  );
  data.sequence = sequencedIndexes;

  // App state
  let newAppState = cloneDeep(appState);

  if (aeonTimelineData && !isNil(aeonTimelineData.openViews)) {
    newAppState.desktopOpenViews = aeonTimelineData.openViews;
  }
  if (aeonTimelineData && !isNil(aeonTimelineData.scrollingIsLocked)) {
    newAppState.scrollingIsLocked = aeonTimelineData.scrollingIsLocked;
  }
  if (aeonTimelineData && !isNil(aeonTimelineData.exposedFilterTypes)) {
    newAppState.exposedFilterTypes = aeonTimelineData.exposedFilterTypes;
  }
  if (aeonTimelineData && !isNil(aeonTimelineData.disableFiltering)) {
    newAppState.disableFiltering = aeonTimelineData.disableFiltering;
  }
  if (aeonTimelineData && !isNil(aeonTimelineData.disableFilteringMore)) {
    newAppState.disableFilteringMore = aeonTimelineData.disableFilteringMore;
  }
  if (aeonTimelineData && !isNil(aeonTimelineData.disableSplitView)) {
    newAppState.disableSplitView = aeonTimelineData.disableSplitView;
  }
  if (aeonTimelineData && !isNil(aeonTimelineData.disableTimelineZoom)) {
    newAppState.disableTimelineZoom = aeonTimelineData.disableTimelineZoom;
  }
  //#if harvard === true
  let preferences = PreferencesRecord().toJS();
  if (aeonTimelineData && !isNil(aeonTimelineData.skin)) {
    preferences.skin = aeonTimelineData.skin;
  }
  //#!endif

  // Setup the initial scrolling position and unit scale for timeline view
  // if there is no initial values
  updateTimelineOptions(newAppState, calendar, definitions, items);

  // Filters
  data.filters = aeonTimelineData.filters;

  // Filters: Replace tag ids with the new ids
  try {
    if (aeonTimelineData && !isNil(aeonTimelineData.filters) && !isNil(aeonTimelineData.tags)) {
      for (const viewId in aeonTimelineData.filters.byId) {
        if (
          !aeonTimelineData.filters.byId[viewId].tags.terms ||
          !aeonTimelineData.filters.byId[viewId].tags.terms.length
        ) {
          continue;
        }

        let newTagIds = [];
        for (const tagId of aeonTimelineData.filters.byId[viewId].tags.terms) {
          const tagText = aeonTimelineData.tags[tagId];

          for (const key in tagData) {
            if (tagData[key] === tagText) {
              newTagIds.push(key);
              break;
            }
          }
        }

        data.filters.byId[viewId].tags.terms = newTagIds;
      }
    }
  } catch (e) {
    /* eslint-disable no-console */
    console.log(e);
  }

  // TO DO: Replace the filter list property option IDs with text on export
  try {
    if (aeonTimelineData && !isNil(aeonTimelineData.filters) && !isNil(aeonTimelineData.properties)) {
      for (const viewId in aeonTimelineData.filters.byId) {
        if (
          !aeonTimelineData.filters.byId[viewId].textProperties ||
          !Object.keys(aeonTimelineData.filters.byId[viewId].textProperties).length
        ) {
          continue;
        }

        for (const key in aeonTimelineData.filters.byId[viewId].textProperties) {
          const val = aeonTimelineData.filters.byId[viewId].textProperties[key];
          if (!val.terms || !val.terms.length) {
            continue;
          }

          const property = aeonTimelineData.properties.byId[key];
          if (property.format === PropertyFormatIdentifier.list) {
            for (const propertyText of val.terms) {
              const newIds = [];
              for (const allowedValue of property.allowed) {
                if (allowedValue.id === propertyText) {
                  const newValue = definitions.properties.byId[key].allowed.find(
                    importedAllowedValue => importedAllowedValue.label === allowedValue.label
                  );
                  newIds.push(newValue.id);
                  break;
                }
              }
              data.filters.byId[viewId].textProperties[key].terms = newIds;
            }
          }
        }
      }
    }
  } catch (e) {
    /* eslint-disable no-console */
    console.log(e);
  }

  const timeline = {
    definitions, // Required
    data, // Required,
    appState: newAppState,
    //#if harvard === true
    preferences: preferences
    //#!endif
  };

  return timeline;
};

export default standardToV3Converter;
